import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './StatChip.css';

/**
 * StatChip - MapleStory-style stat display component
 *
 * Shows stat icon/label with detailed tooltip on hover
 * Displays base stats, bonuses, and formulas
 */
const StatChip = ({
  icon,
  label,
  value,
  tooltip = '',
  base = null,
  bonus = null,
  buffBonus = null, // Buff contribution (separate from equipment)
  potentialBonus = null, // Potential (cubing) contribution
  shrineBonus = null, // Shrine blessing contribution
  total = null,
  isPercentage = false,
  decimals = 0,
  isMultiplier = false,
  type = 'combat', // 'combat' or 'primary'
  onAllocate = null,
  hasPoints = false,
  isHighlighted = false,
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipReady, setTooltipReady] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chipRef = useRef(null);
  const tooltipRef = useRef(null);

  // Format the display value based on type
  const formatValue = (val) => {
    if (val === null || val === undefined) return '';
    if (isPercentage) {
      return `${val.toFixed(decimals)}%`;
    }
    if (isMultiplier) {
      return `${val.toFixed(decimals)}x`;
    }
    return Math.floor(val);
  };

  // Tooltip positioning with overflow detection
  const handleMouseEnter = () => {
    if (!tooltip || !chipRef.current) return;

    // Calculate position immediately before showing
    const chipRect = chipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 10;

    // Estimate tooltip size (will be refined after render)
    const estimatedWidth = 280;
    const estimatedHeight = 200;

    let x = chipRect.right + gap;
    let y = chipRect.top;

    if (x + estimatedWidth > viewportWidth - gap) {
      x = chipRect.left - estimatedWidth - gap;
    }
    if (x < gap) {
      x = viewportWidth - estimatedWidth - gap;
    }
    if (y + estimatedHeight > viewportHeight - gap) {
      y = viewportHeight - estimatedHeight - gap;
    }
    if (y < gap) {
      y = gap;
    }

    setTooltipPosition({ x, y });
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
    setTooltipReady(false);
  };

  // Refine position after tooltip renders with actual dimensions
  React.useEffect(() => {
    if (tooltipVisible && tooltipRef.current && chipRef.current) {
      const chip = chipRef.current;
      const tooltipEl = tooltipRef.current;
      const chipRect = chip.getBoundingClientRect();
      const tooltipRect = tooltipEl.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 10;

      let x = chipRect.right + gap;
      let y = chipRect.top;

      if (x + tooltipRect.width > viewportWidth - gap) {
        x = chipRect.left - tooltipRect.width - gap;
      }
      if (x < gap) {
        x = viewportWidth - tooltipRect.width - gap;
      }
      if (y + tooltipRect.height > viewportHeight - gap) {
        y = viewportHeight - tooltipRect.height - gap;
      }
      if (y < gap) {
        y = gap;
      }

      setTooltipPosition({ x, y });
      // Make tooltip visible after position is calculated
      requestAnimationFrame(() => setTooltipReady(true));
    }
  }, [tooltipVisible]);

  // Determine bonus color (green for positive, red for negative)
  const bonusColor = bonus > 0 ? '#10b981' : bonus < 0 ? '#ef4444' : '#9ca3af';
  const buffColor = '#a78bfa'; // Purple for buff bonuses
  const potentialColor = '#8b5cf6'; // Purple for potential (cubing) bonuses
  const shrineColor = '#fbbf24'; // Gold for shrine blessings
  const hasBonus = bonus !== null && bonus !== undefined && bonus !== 0;
  const hasBuffBonus = buffBonus !== null && buffBonus !== undefined && Math.abs(buffBonus) > 0.001;
  const hasPotentialBonus = potentialBonus !== null && potentialBonus !== undefined && Math.abs(potentialBonus) > 0.001;
  const hasShrineBonus = shrineBonus !== null && shrineBonus !== undefined && Math.abs(shrineBonus) > 0.001;
  // Always show base if base is provided (even without equipment bonuses)
  const showBaseStats = base !== null && base !== undefined;

  // Render tooltip content
  const renderTooltip = () => {
    if (!tooltip || !tooltipVisible) return null;

    const lines = tooltip.split('\n');
    const description = lines[0] || '';
    const formula = lines[1] || '';

    return (
      <div
        ref={tooltipRef}
        className="stat-tooltip-ms"
        style={{
          position: 'fixed',
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          opacity: tooltipReady ? 1 : 0,
        }}
      >
        <div className="stat-tooltip-desc">{description}</div>
        {formula && (
          <div className="stat-tooltip-formula">{formula}</div>
        )}
        {showBaseStats && (
          <div className="stat-tooltip-breakdown">
            <div className="breakdown-row">
              <span className="breakdown-label">Base:</span>
              <span className="breakdown-value">{formatValue(base)}</span>
            </div>
            {hasBonus && (
              <div className="breakdown-row">
                <span className="breakdown-label">Equipment:</span>
                <span className="breakdown-value" style={{ color: bonusColor }}>
                  {bonus > 0 ? '+' : ''}{formatValue(bonus)}
                </span>
              </div>
            )}
            {hasPotentialBonus && (
              <div className="breakdown-row">
                <span className="breakdown-label">Potential:</span>
                <span className="breakdown-value" style={{ color: potentialColor }}>
                  +{formatValue(potentialBonus)}%
                </span>
              </div>
            )}
            {hasBuffBonus && (
              <div className="breakdown-row">
                <span className="breakdown-label">Buffs:</span>
                <span className="breakdown-value" style={{ color: buffColor }}>
                  {buffBonus > 0 ? '+' : ''}{formatValue(buffBonus)}
                </span>
              </div>
            )}
            {hasShrineBonus && (
              <div className="breakdown-row">
                <span className="breakdown-label">Shrines:</span>
                <span className="breakdown-value" style={{ color: shrineColor }}>
                  +{formatValue(shrineBonus)}
                </span>
              </div>
            )}
            {(hasBonus || hasBuffBonus || hasPotentialBonus || hasShrineBonus) && (
              <div className="breakdown-row breakdown-total">
                <span className="breakdown-label">Total:</span>
                <span className="breakdown-value">{formatValue(total)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (type === 'primary') {
    const hasPrimaryBonuses = hasShrineBonus || hasPotentialBonus;
    return (
      <>
        <div
          ref={chipRef}
          className="stat-chip stat-chip-primary"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className="stat-chip-icon">{icon}</span>
          <div className="stat-chip-info">
            <span className="stat-chip-label">{label}</span>
            <span className="stat-chip-name">{label.substring(0, 3).toUpperCase()}</span>
          </div>
          <div className="stat-chip-value-section">
            <span className="stat-chip-value">{formatValue(base !== null ? base : value)}</span>
            {hasShrineBonus && (
              <span className="stat-shrine-primary" style={{ color: shrineColor }}>
                {' '}(+{formatValue(shrineBonus)})
              </span>
            )}
            {hasPotentialBonus && (
              <span className="stat-potential-primary" style={{ color: potentialColor }}>
                {' '}(+{formatValue(potentialBonus)}%)
              </span>
            )}
            {hasPrimaryBonuses && (
              <>
                <span className="stat-equals-primary"> = </span>
                <span className="stat-total-primary">{formatValue(total)}</span>
              </>
            )}
          </div>
          {hasPoints && onAllocate && (
            <button className="stat-chip-allocate" onClick={onAllocate} title={`Add 1 point to ${label}`}>
              +
            </button>
          )}
        </div>
        {tooltipVisible && createPortal(renderTooltip(), document.body)}
      </>
    );
  }

  // Combat stat chip (compact)
  return (
    <>
      <div
        ref={chipRef}
        className={`stat-chip stat-chip-combat ${isHighlighted ? 'stat-chip-highlighted' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="stat-chip-left">
          <span className="stat-chip-icon">{icon}</span>
          <span className="stat-chip-label">{label}</span>
        </div>
        <div className="stat-chip-right">
          {showBaseStats ? (
            <>
              <span className="stat-base">{formatValue(base)}</span>
              {hasBonus && (
                <span className="stat-bonus" style={{ color: bonusColor }}>
                  {' '}({bonus > 0 ? '+' : ''}{formatValue(bonus)})
                </span>
              )}
              {hasPotentialBonus && (
                <span className="stat-potential" style={{ color: potentialColor }}>
                  {' '}(+{formatValue(potentialBonus)}%)
                </span>
              )}
              {hasBuffBonus && (
                <span className="stat-buff" style={{ color: buffColor }}>
                  {' '}({buffBonus > 0 ? '+' : ''}{formatValue(buffBonus)})
                </span>
              )}
              {hasShrineBonus && (
                <span className="stat-shrine" style={{ color: shrineColor }}>
                  {' '}(+{formatValue(shrineBonus)})
                </span>
              )}
              {(hasBonus || hasBuffBonus || hasPotentialBonus || hasShrineBonus) && (
                <>
                  <span className="stat-equals"> = </span>
                  <span className="stat-total">{formatValue(total)}</span>
                </>
              )}
            </>
          ) : (
            <span className="stat-value">{formatValue(value)}</span>
          )}
        </div>
      </div>
      {tooltipVisible && createPortal(renderTooltip(), document.body)}
    </>
  );
};

export default StatChip;
